import type { Goal } from "api/types/aba";
import type { TargetDisposition } from "api/goals";
import type { TargetActionState } from "../Goals/GoalDispositionPanel";
import type { GoalTarget } from "../Targets/GoalTargetsList";
import type { ClinicalPhase } from "../shared/types";
import { formatSessionDate, getLastSessionDate, getSessionCount } from "./sessionHelpers";

/**
 * Selectors that derive view-ready data from API entities.
 * shared across multiple views (ListGoals, GoalDetails).
 */

// ---------------------------------------------------------------------------
// Option-shape selectors
// ---------------------------------------------------------------------------

export interface TargetOption {
  id: string;
  name: string;
}

export interface GoalOption {
  id: string;
  name: string;
}

/**
 * Non-archived targets reduced to the `{id, name}` shape consumed by
 * ArchiveGoalPanel / DeleteGoalPanel for per-target disposition selection.
 */
export function selectActiveTargetOptions(targets: Goal["targets"] | undefined): TargetOption[] {
  return (targets ?? [])
    .filter((t) => !t.is_archived)
    .map((t) => ({ id: String(t.id), name: t.title }));
}

/**
 * Goals available as a destination for moving targets out of `excludeGoalId`
 * during goal archive/delete. Only excludes the source goal — callers needing
 * stricter compatibility (same behavior_subject + goal_type) should use
 * `selectMoveDestinationGoals` instead.
 */
export function selectGoalsForMove(goals: Goal[] | undefined, excludeGoalId: number): GoalOption[] {
  return (goals ?? [])
    .filter((g) => g.id !== excludeGoalId)
    .map((g) => ({ id: String(g.id), name: g.title }));
}

/**
 * Destination goals for moving a single target out of `sourceGoal`.
 * Business rule: same client, non-archived, and matching `behavior_subject`
 * + `goal_type` so the moved target remains valid in its new parent.
 */
export function selectMoveDestinationGoals(
  goals: Goal[] | undefined,
  sourceGoal: Pick<Goal, "id" | "behavior_subject" | "goal_type"> | null | undefined
): GoalOption[] {
  if (!sourceGoal) return [];
  return (goals ?? [])
    .filter(
      (g) =>
        g.id !== sourceGoal.id &&
        !g.is_archived &&
        g.behavior_subject === sourceGoal.behavior_subject &&
        g.goal_type === sourceGoal.goal_type
    )
    .map((g) => ({ id: String(g.id), name: g.title }));
}

// ---------------------------------------------------------------------------
// Disposition payload builder
// ---------------------------------------------------------------------------

/**
 * Converts the panel's `TargetActionState[]` into the `TargetDisposition[]`
 * payload accepted by archive/delete goal endpoints.
 *
 * `fallbackAction` is applied to actions that are not "move" — pass
 * "archive" from ArchiveGoalPanel and "delete" from DeleteGoalPanel.
 */
export function buildTargetDispositions(
  actions: TargetActionState[],
  fallbackAction: "archive" | "delete"
): TargetDisposition[] {
  return actions.map((a) =>
    a.action === "move"
      ? {
          target_id: Number(a.targetId),
          action: "move" as const,
          destination_goal_id: Number(a.destinationGoalId),
        }
      : { target_id: Number(a.targetId), action: fallbackAction }
  );
}

// ---------------------------------------------------------------------------
// GoalTargetsList row mapper
// ---------------------------------------------------------------------------

/**
 * Maps API goal targets to rows for `GoalTargetsList`. Archived targets are
 * included (rendered dimmed) — callers needing only active targets should
 * filter on `isArchived`.
 *
 * Display rule: archived targets show "discontinued" as their status — purely
 * visual, the API status is untouched.
 *
 */
export function selectTargetListRows(targets: Goal["targets"] | undefined): GoalTarget[] {
  return (targets ?? []).map((t) => ({
    id: String(t.id),
    name: t.title,
    sessionCount: getSessionCount(t.sessions),
    lastSessionDate: formatSessionDate(getLastSessionDate(t.sessions)),
    status: t.is_archived ? "discontinued" : t.status,
    clinicalPhase: t.phase as ClinicalPhase,
    isArchived: Boolean(t.is_archived),
  }));
}
